import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  DialogTitle,
  Grid,
  Divider,
  TextField,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import invoiceApi from "../api/invoiceApi";

const InlineInput = ({
  value,
  onChange,
  width = "50px",
  textAlign = "center",
  readonly = false,
  //placeholder = "...",
}) => (
  <input
    value={value}
    onChange={onChange}
    style={{
      width: width,
      border: "none",
      borderBottom: "1px dotted #000",
      outline: "none",
      fontFamily: "Times New Roman",
      fontSize: "14pt",
      textAlign: textAlign,
      backgroundColor: "transparent",
      margin: "0 2px",
    }}
    readOnly={readonly}
    //placeholder={placeholder}
  />
);
// Styled component cho các ô nhập liệu dạng dấu chấm
const DotInput = ({
  label,
  value,
  onChange,
  fontWeight = "normal",
  //placeholder = "..................................................",
}) => (
  <Box sx={{ display: "flex", alignItems: "flex-end", mb: 1, width: "100%" }}>
    <Typography
      sx={{
        fontWeight: fontWeight,
        whiteSpace: "nowrap",
        mr: 1,
        fontSize: "14pt",
      }}
    >
      {label}
    </Typography>
    <input
      value={value}
      onChange={onChange}
      style={{
        flexGrow: 1,
        border: "none",
        borderBottom: "1px dotted #000",
        outline: "none",
        fontFamily: "Times New Roman",
        fontSize: "14pt",
        padding: "0 5px",
      }}
      //placeholder={placeholder}
    />
  </Box>
);

const InlineDatePicker = ({ value, onChange }) => (
  <DatePicker
    value={value}
    onChange={onChange}
    format="DD/MM/YYYY"
    slotProps={{
      textField: {
        variant: "standard",
        sx: {
          width: "150px",
          mx: 1,
          "& .MuiInput-root": {
            fontFamily: '"Times New Roman", Times, serif',
            fontSize: "12pt",
            "&:before, &:after": { borderBottom: "1px dotted #000" },
          },
          "& .MuiInputBase-input": { textAlign: "center", p: 0 },
        },
      },
    }}
  />
);

const AgreementModal = ({ open, handleClose, reiId }) => {
  const [formData, setFormData] = useState({
    maId: null,
    maDate: dayjs(),
    maNumber: null,
    maContract: null,
    reiId: null,
    reiDate: dayjs(),
    reiInvoiceNum: null,
    reiSign: null,
    reiTemplate: null,
    reiDateParent: dayjs(),
    reiInvoiceNumParent: null,
    reiSignParent: null,
    reiTemplateParent: null,
    reiReason: null,
    reiStatus: 1,
    customerCompanyName: null,
    customerTaxCode: null,
    customerAddress: null,
    customerRepresentative: null,
    customerPosition: null,
    customerEmail: null,
    customerPhone: null,
    buyerCompanyName: null,
    buyerTaxCode: null,
    buyerAddress: null,
    buyerRepresentative: null,
    buyerPosition: null,
    buyerEmail: null,
    buyerPhone: null,
  });

  useEffect(() => {
    async function fetchMemorandumAgreement() {
      try {
        const response = await invoiceApi.getMemorandumAgreement(reiId);
        if (response.success) {
          setFormData(response.data);
        } else {
          toast.error(response.message);
          setFormData({});
        }
      } catch (error) {
        toast.error(error);
        setFormData({});
      }
    }
    if (open && reiId) fetchMemorandumAgreement();
  }, [open, reiId]);

  const handleSave = async () => {
    try {
      const response = await invoiceApi.SaveMemorandumAgreement(formData);
      if (response.success) {
        toast.success("Thành công");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error);
    }
  };

  if (!open) return null;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="vi">
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { fontFamily: '"Times New Roman", Times, serif' },
        }}
      >
        <DialogTitle>
          <Grid sx={{ display: "flex", justifyContent: "space-between" }}>
            <Grid>
              <Typography variant="h5">Biên bản thỏa thuận</Typography>
            </Grid>
            <Grid>
              {formData?.maId && (
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                >
                  Tải về
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ color: "#000" }}>
          {/* Quốc hiệu */}
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "13pt" }}>
              CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM
            </Typography>
            <Typography sx={{ fontSize: "14pt" }}>
              Độc lập - Tự do - Hạnh phúc
            </Typography>
            <Box
              sx={{
                width: "150px",
                borderBottom: "1px solid #000",
                margin: "5px auto",
              }}
            />
          </Box>

          {/* Tiêu đề */}
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography sx={{ fontWeight: "bold", fontSize: "14pt" }}>
              BIÊN BẢN THỎA THUẬN LẬP HÓA ĐƠN ĐIỆN TỬ{" "}
              {formData?.reiStatus === 2 ? "THAY THẾ" : "ĐIỀU CHỈNH"}
              <br />
              CHO HÓA ĐƠN ĐIỆN TỬ CÓ SAI SÓT
            </Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontStyle: "italic", fontWeight: "bold" }}>
                (Số:
              </Typography>
              <input
                style={{
                  border: "none",
                  borderBottom: "1px dotted #000",
                  outline: "none",
                  width: "80px",
                  textAlign: "center",
                }}
                value={formData?.maNumber || ""}
                onChange={(e) =>
                  setFormData({ ...formData, maNumber: e.target.value })
                }
              />
              <Typography sx={{ fontStyle: "italic", fontWeight: "bold" }}>
                )
              </Typography>
            </Box>
          </Box>

          {/* Căn cứ */}
          <Box
            sx={{
              fontStyle: "italic",
              fontSize: "12pt",
              mb: 2,
              textAlign: "justify",
            }}
          >
            <Typography sx={{ display: "flex" }}>
              Căn cứ vào hợp đồng:
              <input
                style={{
                  flexGrow: 1,
                  border: "none",
                  borderBottom: "1px dotted #000",
                  outline: "none",
                  paddingLeft: "5px",
                }}
                value={formData?.maContract || ""}
                onChange={(e) =>
                  setFormData({ ...formData, maContract: e.target.value })
                }
              />
            </Typography>
            <Typography fontSize="inherit">
              - Căn cứ Nghị định 123/2020/NĐ-CP ngày 19/10/2020 của Chính phủ
              quy định về hóa đơn, chứng từ.
            </Typography>
            <Typography fontSize="inherit">
              - Căn cứ Nghị định 70/2025/NĐ-CP ngày 20/03/2025 của Chính phủ sửa
              đổi, bổ sung một số điều của Nghị định 123/2020/NĐ-CP ngày
              19/10/2020 của Chính phủ quy định về hóa đơn, chứng từ.
            </Typography>
            <Typography fontSize="inherit">
              - Căn cứ Thông tư 32/2025/TT-BTC ngày 31/05/2025 của Bộ Tài chính
              hướng dẫn thực hiện Nghị định số 123/2020/NĐ-CP ngày 19/10/2020
              của Chính phủ quy định về hóa đơn, chứng từ, Nghị định số
              70/2025/NĐ-CP ngày 20/03/2025 sửa đổi, bổ sung một số điều Nghị
              định số 123/2020/NĐ-CP.
            </Typography>
          </Box>

          {/* Nội dung chính */}
          <Box sx={{ fontSize: "12pt" }}>
            <Typography sx={{ mb: 2 }}>
              Hôm nay, ngày{" "}
              <InlineDatePicker
                value={dayjs(formData?.maDate)}
                onChange={(val) => setFormData({ ...formData, maDate: val })}
              />
              hai bên chúng tôi gồm có:
            </Typography>

            <DotInput
              label="Đơn vị bán hàng:"
              fontWeight="bold"
              value={formData?.customerCompanyName}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  customerCompanyName: e.target.value,
                })
              }
            />
            <DotInput
              label="Mã số thuế:"
              value={formData?.customerTaxCode}
              onChange={(e) =>
                setFormData({ ...formData, customerTaxCode: e.target.value })
              }
            />
            <DotInput
              label="Địa chỉ:"
              value={formData?.customerAddress}
              onChange={(e) =>
                setFormData({ ...formData, customerAddress: e.target.value })
              }
            />
            <DotInput
              label="Đại diện:"
              value={formData?.customerRepresentative}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  customerRepresentative: e.target.value,
                })
              }
            />
            <DotInput
              label="Chức vụ:"
              value={formData?.customerPosition}
              onChange={(e) =>
                setFormData({ ...formData, customerPosition: e.target.value })
              }
            />

            <Box sx={{ height: 20 }} />

            <DotInput
              label="Đơn vị mua hàng:"
              fontWeight="bold"
              value={formData?.buyerCompanyName}
              onChange={(e) =>
                setFormData({ ...formData, buyerCompanyName: e.target.value })
              }
            />
            <DotInput
              label="Mã số thuế:"
              value={formData?.buyerTaxCode}
              onChange={(e) =>
                setFormData({ ...formData, buyerTaxCode: e.target.value })
              }
            />
            <DotInput
              label="Địa chỉ:"
              value={formData?.buyerAddress}
              onChange={(e) =>
                setFormData({ ...formData, buyerAddress: e.target.value })
              }
            />
            <DotInput
              label="Đại diện:"
              value={formData?.buyerRepresentative}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  buyerRepresentative: e.target.value,
                })
              }
            />
            <DotInput
              label="Chức vụ:"
              value={formData?.buyerPosition}
              onChange={(e) =>
                setFormData({ ...formData, buyerPosition: e.target.value })
              }
            />

            <Typography sx={{ mt: 2, textAlign: "justify" }}>
              Hai bên thống nhất lập hóa đơn{" "}
              {formData?.reiStatus === 2 ? "thay thế" : "điều chỉnh"} Mẫu số
              <InlineInput value={formData?.reiTemplate} readonly={true} />, ký
              hiệu{" "}
              <InlineInput
                value={formData?.reiSign}
                readonly={true}
                width="100px"
              />
              , số{" "}
              <InlineInput
                value={formData?.reiInvoiceNum}
                readonly={true}
                width="100px"
              />
              , ngày
              <InlineInput
                value={dayjs(formData?.reiDate).format("DD/MM/YYYY")}
                readonly={true}
                width="150px"
              />{" "}
              để {formData?.reiStatus === 2 ? "thay thế" : "điều chỉnh"} cho hóa
              đơn sai sót Mẫu số
              <InlineInput
                value={formData?.reiTemplateParent}
                readonly={true}
              />
              , ký hiệu{" "}
              <InlineInput
                value={formData?.reiSignParent}
                readonly={true}
                width="100px"
              />
              , số{" "}
              <InlineInput
                value={formData?.reiInvoiceNumParent}
                readonly={true}
                width="100px"
              />
              , ngày
              <InlineInput
                value={dayjs(formData?.reiDateParent).format("DD/MM/YYYY")}
                readonly={true}
                width="150px"
              />
              .
            </Typography>

            <Box sx={{ mt: 2, display: "flex" }}>
              <Typography fontWeight="bold" sx={{ minWidth: 130 }}>
                {formData?.reiStatus === 2
                  ? "Lý do thay thế:"
                  : "Lý do điều chỉnh:"}
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData?.reiReason}
                onChange={(e) =>
                  setFormData({ ...formData, reiReason: e.target.value })
                }
              />
              {/* <DotInput
                label={
                  formData?.reiStatus === 2
                    ? "Lý do thay thế:"
                    : "Lý do điều chỉnh:"
                }
                fontWeight="bold"
                value={formData?.reiReason}
                onChange={(e) =>
                  setFormData({ ...formData, reiReason: e.target.value })
                }
              /> */}
            </Box>
          </Box>

          {/* Chữ ký */}
          {/* <Grid container sx={{ mt: 4, textAlign: "center", fontWeight: "bold" }}>
          <Grid item xs={6}>
            ĐẠI DIỆN BÊN MUA
            <Typography
              sx={{
                fontSize: "10pt",
                fontStyle: "italic",
                fontWeight: "normal",
              }}
            >
              (Ký điện tử/ ký, đóng dấu...)
            </Typography>
          </Grid>
          <Grid item xs={6}>
            ĐẠI DIỆN BÊN BÁN
            <Typography
              sx={{
                fontSize: "10pt",
                fontStyle: "italic",
                fontWeight: "normal",
              }}
            >
              (Ký điện tử/ ký, đóng dấu...)
            </Typography>
          </Grid>
        </Grid> */}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} variant="outlined" color="inherit">
            Hủy
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Lưu
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default AgreementModal;
